import { create } from 'zustand';
import type { DeviceStatic } from './types';

/**
 * Static device data from the bootstrap snapshot. Held in a Map that changes only on registry changes;
 * `version` increments ONLY when content actually changed (a resync with identical data is a no-op),
 * so whole-set consumers (map source) never rebuild because of a reconnect.
 */
export const deviceStatic = new Map<string, DeviceStatic>();

export const useStaticVersion = create<{ version: number; loaded: boolean }>(() => ({
  version: 0,
  loaded: false,
}));

/**
 * Same static content, field by field over every field either side has (a field added later, such as the project,
 * cannot be forgotten here: 0.53.0 ignored project changes until a page reload). Nested values (capabilities,
 * upstream) are compared by content.
 */
export const same = (a: DeviceStatic, b: DeviceStatic) => {
  for (const k of new Set([...Object.keys(a), ...Object.keys(b)]) as Set<keyof DeviceStatic>) {
    const x = a[k];
    const y = b[k];
    if (x === y) continue;
    if (typeof x === 'object' && typeof y === 'object' && JSON.stringify(x) === JSON.stringify(y)) continue;
    return false;
  }
  return true;
};

export function loadStatic(items: Iterable<DeviceStatic>): boolean {
  let changed = false;
  for (const d of items) {
    const cur = deviceStatic.get(d.id);
    if (cur && same(cur, d)) continue;
    deviceStatic.set(d.id, d);
    changed = true;
  }
  if (changed) useStaticVersion.setState((s) => ({ version: s.version + 1, loaded: true }));
  else if (!useStaticVersion.getState().loaded) useStaticVersion.setState({ loaded: true });
  return changed;
}

/** The device left the registry (back to Discovered): forget it; map and lists rebuild from the static version. */
export function removeStatic(id: string): boolean {
  if (!deviceStatic.delete(id)) return false;
  useStaticVersion.setState((s) => ({ version: s.version + 1 }));
  return true;
}

/** Sign-out: forget every device of the previous session. */
export function clearStatic(): void {
  deviceStatic.clear();
  useStaticVersion.setState((s) => ({ version: s.version + 1, loaded: false }));
}

let dependantsCache: { version: number; map: Map<string, string[]> } | null = null;

/**
 * Devices reached through each device (upstream id → dependant ids), derived from the static registry and rebuilt
 * only when it changes (version). Never stored: the relation lives on the dependant (`upstream`).
 */
export function dependantsIndex(): Map<string, string[]> {
  const version = useStaticVersion.getState().version;
  if (dependantsCache?.version === version) return dependantsCache.map;
  const map = new Map<string, string[]>();
  for (const d of deviceStatic.values())
    if (d.upstream) {
      const list = map.get(d.upstream.id);
      if (list) list.push(d.id);
      else map.set(d.upstream.id, [d.id]);
    }
  dependantsCache = { version, map };
  return map;
}
